<template>
  <div class="comp-detail">
    <!-- Header -->
    <detail-page-header
      :title="isNegotiationMode ? 'Alternative Site' : comp.building.address_line_1"
      :subtitle="`${comp.building.city}, ${comp.building.state}`"
      :show-back-button="!fromReatlas"
      :show-close-button="fromReatlas"
      :is-modal="fromReatlas"
      color-variant="green"
      back-button-label="Back to Project"
      close-button-label="Close panel"
      @back="goBackToProject"
      @close="$emit('reatlas-close-right-panel')"
    >
      <template #actions>
        <a 
          v-if="fromReatlas" 
          target="_blank"
          :href="`/projects/${project.id}/comps/${comp.id}`"
          class="comp-detail__reatlas-link"
        >
          View Full Details
        </a>

        <div class="comp-detail__status-actions">
          <button
            class="comp-detail__action-button"
            :class="{ 'comp-detail__action-button--active': comp.is_rejected }"
            @click="toggleRejected()"
            title="Toggle Rejected"
          >
            <theme-icon icon="minus" type="icons" classes="w-5 h-5" />
          </button>

          <button
            class="comp-detail__action-button"
            :class="{ 'comp-detail__action-button--active': comp.is_favorite }"
            @click="toggleFavorite()"
            title="Toggle Favorite"
          >
            <theme-icon icon="favorite" type="icons" classes="w-5 h-5" />
          </button>

          <button
            v-if="$gate.isAdmin()"
            class="comp-detail__action-button"
            :title="researchStatusTitle"
          >
            <v-popover trigger="click" offset="10" ref="popover">
              <theme-icon icon="plus" type="icons" classes="w-5 h-5" />

              <template slot="popover">
                <div class="comp-detail__research-popover">
                  <p class="comp-detail__research-title">
                    Change the <strong>Research Status</strong>
                  </p>
                  <div class="comp-detail__research-options">
                    <button
                      v-for="status in researchStatuses"
                      :key="status.slug"
                      class="comp-detail__research-option"
                      :class="{ 'comp-detail__research-option--active': comp.research_status == status.slug }"
                      @click="changeResearchStatus(status.slug)"
                    >
                      <theme-icon :icon="status.slug" type="icons" classes="w-5 h-5" />
                      <span>{{ status.label }}</span>
                    </button>
                  </div>
                </div>
              </template>
            </v-popover>
          </button>
          <button
            v-else
            class="comp-detail__action-button"
            :title="researchStatusTitle"
          >
            <theme-icon icon="plus" type="icons" classes="w-5 h-5" />
          </button>

          <div class="comp-detail__score" :class="comp.score_color">
            {{ comp.score }}
          </div>
        </div>
      </template>
    </detail-page-header>

    <!-- Main Content -->
    <div class="comp-detail__content" :class="{ 'comp-detail__content--modal': fromReatlas }">
      <div class="comp-detail__media-section">
        <div class="comp-detail__gallery">
          <media-gallery-v1
            :documentable-id="comp.building.id"
            documentable-type="App\Building"
            :media="comp.building.media"
            :height="500"
            :object-cover="!fromReatlas"
          />
        </div>
        <div v-if="!fromReatlas" class="comp-detail__map">
          <map-component
            :latitude="comp.building.latitude"
            :longitude="comp.building.longitude"
            :zoom="6"
          />
        </div>
      </div>

      <!-- Tabs -->
      <div class="comp-detail__tabs-wrapper">
        <div v-if="fromReatlas" class="comp-detail__tab-select">
          <label for="section-select" class="comp-detail__tab-select-label">Viewing Section</label>
          <themed-select
            id="section-select"
            v-model="selectedTab"
            :options="tabOptions"
            aria-label="Select section to view"
          />
        </div>

        <nav v-if="!fromReatlas" class="comp-detail__tabs" aria-label="Comp sections">
          <button
            v-for="(tab, slug) in enabledTabs"
            :key="slug"
            @click="selectedTab = slug"
            class="comp-detail__tab"
            :class="{ 'comp-detail__tab--active': slug == selectedTab }"
          >
            {{ tab.label }}
            <span v-if="tab.showCount" class="comp-detail__tab-count">
              {{ tab.count }}
            </span>
          </button>
        </nav>

        <div class="comp-detail__tab-content" :class="{ 'comp-detail__tab-content--bordered': !fromReatlas }">
          <comp-score-tab
            v-if="selectedTab == 'score'"
            :project-id="project.id"
            :scorable-id="comp.id"
            scorable-type="App\ProjectComp"
            :data="project.ksd_scenarios"
            :is-modal="false"
            @comp-score-updated="updateCompScore($event)"
            :show-chart="true"
            :from-reatlas="fromReatlas"
          />

          <building-fields
            v-if="selectedTab == 'building'"
            :building-data="comp.building"
            :from-reatlas="fromReatlas"
          />

          <units
            v-if="selectedTab == 'units'"
            show-edit-actions
            show-chart
          />

          <documents
            v-if="selectedTab == 'documents'"
            :document-data="comp.building.documents"
            :documentable-id="comp.building_id"
            documentable-type="App\Building"
            :from-reatlas="fromReatlas"
          />

          <contacts
            v-if="selectedTab == 'contacts'"
            :contact-data="comp.building.contacts"
            attach-to="building"
            :attach-to-id="comp.building_id"
          />

          <comments
            v-if="selectedTab == 'comments'"
            :data="comp.comments"
            :document-data="comp.building.documents"
            :commentable-id="comp.id"
            commentable-type="App\ProjectComp"
          />

          <!-- <comp-effective-rent-tab
            v-if="selectedTab == 'effective_rent_calculator'"
            :project-id="comp.project_id"
            :data="comp.effective_rent_calculation"
            :from-reatlas="false"
          />

          <comp-lease-vs-purchase-tab
            v-if="selectedTab == 'lease_vs_purchase_calculator'"
            :comp-data="comp"
            :calculation-data="comp.lease_vs_purchase_calculation"
          /> -->

          <div v-if="$gate.isAdmin()">
            <change-log
              v-if="selectedTab == 'changelog'"
              :data="comp.audits"
            />
          </div>
        </div>
      </div>
    </div>

    <notifications classes="notification" position="bottom right" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import ThemedSelect from '../ThemedSelect.vue';
import ThemeIcon from '../ThemeIcon.vue';
import DetailPageHeader from '../DetailPageHeader.vue';
import CompScoreTab from './CompScoreTab.vue';
import MediaGalleryV1 from '../MediaGallery.vue';
import Units from '../units/Units.vue';

export default {
  name: 'CompDetail',

  components: {
    ThemedSelect,
    ThemeIcon,
    DetailPageHeader,
    CompScoreTab,
    MediaGalleryV1,
    Units
  },

  props: {
    projectData: {
      type: Object,
      required: true
    },

    compData: {
      type: Object,
      required: true
    },

    openTab: {
      type: String,
      default: 'score'
    },

    fromReatlas: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedTab: this.openTab,

      researchStatuses: [
        {
          slug: 'not-researched',
          label: 'Not Researched'
        },
        {
          slug: 'researching',
          label: 'Researching'
        },
        {
          slug: 'research-complete',
          label: 'Research Complete'
        }
      ]
    };
  },

  mounted() {
    this.projects.selectedProject = this.projectData;
    this.documents.all = this.comp.building.documents;
    this.documents.filtered = this.comp.building.documents;
    this.buildings.all.push(this.compData);
    this.buildings.filtered.push(this.compData);
    this.buildings.selectedBuildingId = this.compData.building_id;
    this.buildings.selectedBuilding = this.compData.building;
    this.units.all = this.compData.building.units;
    this.units.filtered = this.compData.building.units;
    this.ksds.scenarios = this.projectData.ksd_scenarios;
    this.ksds.selectedScenarioId = this.ksds.scenarios[0].id || null;
  },

  computed: {
      enabledTabs() {
          // v-for used to be paired with v-if="tab.enabled"; Vue 2 runs
          // v-for first so the list was already filtered. Same result, and
          // it does not break when the directive priority reverses in Vue 3.
          const source = this.tabs;
          return Array.isArray(source)
              ? source.filter(entry => entry.enabled)
              : Object.fromEntries(Object.entries(source || {}).filter(([, entry]) => entry.enabled));
      },

    ...mapState(['projects', 'buildings', 'documents', 'units', 'ksds']),
    ...mapGetters(['scenario', 'scenarioKsds']),

    isNegotiationMode() {
      return this.scenario?.negotiation_mode;
    },

    project() {
      return this.projectData;
    },

    comp() {
      return this.compData;
    },

    tabs() {
      return {
        score: {
          label: 'Score',
          enabled: true,
          showCount: false
        },
        building: {
          label: 'Building',
          enabled: true,
          showCount: false
        },
        units: {
          label: 'Units',
          enabled: true,
          showCount: true,
          count: this.comp.building.units?.length || 0
        },
        documents: {
          label: 'Documents',
          enabled: true,
          showCount: true,
          count: this.documents.all.length || 0
        },
        contacts: {
          label: 'Contacts',
          enabled: true,
          showCount: true,
          count: this.comp.building.contacts?.length || 0
        },
        comments: {
          label: 'Comments',
          enabled: true,
          showCount: true,
          count: this.comp.comments?.length || 0
        },
        // effective_rent_calculator: {
        //   label: 'Eff. Rent Calculator',
        //   enabled: true,
        //   showCount: false
        // },
        // lease_vs_purchase_calculator: {
        //   label: 'Lease vs. Purchase',
        //   enabled: true,
        //   showCount: false
        // },
        changelog: {
          label: 'Changelog',
          enabled: this.$gate.isAdmin() && !this.fromReatlas,
          showCount: true,
          count: this.comp.audits?.length || 0
        }
      };
    },

    tabOptions() {
      return Object.entries(this.tabs)
        .filter(([slug, tab]) => tab.enabled)
        .map(([slug, tab]) => ({
          value: slug,
          label: tab.showCount ? `${tab.label} (${tab.count})` : tab.label
        }));
    },

    researchStatusTitle() {
      return this.comp.research_status
        .replace(/-/g, ' ')
        .replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
    }
  },

  methods: {
    goBackToProject() {
      window.location.href = `/projects/${this.project.id}`;
    },

    updateComp(params) {
      axios
        .patch('/projects/' + this.project.id + '/comps/' + this.comp.id, params)
        .then(response => {
          this.$notify({
            type: 'success',
            title: 'Success',
            text: response.data.message
          });
        })
        .catch(response => {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: response.data.message
          });
        });
    },

    updateCompScore(score) {
      this.comp.score = score;
      this.setScoreColor();
      this.$forceUpdate();
    },

    setScoreColor() {
      this.comp.score_color =
        this.comp.score < 70
          ? 'comp-detail__score--low'
          : this.comp.score >= 90
          ? 'comp-detail__score--high'
          : 'comp-detail__score--medium';
    },

    toggleFavorite() {
      const favorited_at = this.comp.is_favorite
        ? null
        : moment().format('Y-MM-D H:m:ss');
      const favorited_by = this.comp.is_favorite ? null : window.user.id;

      this.comp.favorited_at = favorited_at;
      this.comp.favorited_by = favorited_by;
      this.comp.is_favorite = favorited_at ? true : false;

      this.updateComp({
        favorited_at: favorited_at,
        favorited_by: favorited_by
      });
    },

    toggleRejected() {
      const rejected_at = this.comp.is_rejected
        ? null
        : moment().format('Y-MM-D H:m:ss');
      const rejected_by = this.comp.is_rejected ? null : window.user.id;

      this.comp.rejected_at = rejected_at;
      this.comp.rejected_by = rejected_by;
      this.comp.is_rejected = rejected_at ? true : false;

      this.updateComp({
        rejected_at: rejected_at,
        rejected_by: rejected_by
      });
    },

    changeResearchStatus(status) {
      this.comp.research_status = status;

      this.updateComp({
        research_status: status
      });

      this.$refs.popover.isOpen = false;
    }
  }
};
</script>
